import { Avatar, Box, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import React, { useState } from 'react';
import './OneOnOneSessionDetails.css';
import OneOnOneSessionIcon from '../../../assets/Dashboard/oneoneonesession.svg';
import GroupSessionIcon from '../../../assets/Dashboard/group.svg';
import PrimeSessionIcon from '../../../assets/Dashboard/prime.svg';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import RescheduleIcon from '../../../assets/SessionIcon/reschedule-icon.svg';
import CancelSessionIcon from '../../../assets/SessionIcon/cancel-session-icon.svg';
import ReportIcon from '../../../assets/SessionIcon/report-issue-icon.svg';
import { useNavigate } from 'react-router-dom';
import DocumentLogo from '../../../assets/SessionIcon/document-icon.svg';
import { useTranslation } from '../../../i18n/LanguageContext';
import dayjs from 'dayjs';
import ZoomIcon from '../../../assets/SessionIcon/zoom-icon.svg';
import ChatIcon from '../../../assets/SidebarIcon/chat-icon.svg';
import RescheduleSessionModal from '../RescheduleSessionModal/RescheduleSessionModal';
import CancelSessionModal from '../CancelSessionModal/CancelSessionModal';
import ReportModal from '../ReportModal/ReportModal';
import StarIcon from '@mui/icons-material/Star';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import { axiosInstance } from '../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';

// const SessionTypeColor = {
//   one_on_one: {
//     color: '#0A5EF3',
//     bgcolor: '#E8F0FF',
//     value: 'One-On-One Session',

//   },
//   group: {
//     color: '#7BCF8C',
//     bgcolor: '#FAFFFB',
//     value: 'Group Session',

//   },
//   prime: {
//     color: '#AD8B00',
//     bgcolor: '#F0EFFF',
//     value: 'Prime Session',
//     icon: PrimeSessionIcon,
//   },
// };
const SessionTypeColor = {
  one_on_one: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    icon: OneOnOneSessionIcon,
    value: 'One-On-One Session',
  },
  group: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    icon: GroupSessionIcon,
    value: 'Group Session',
  },
  prime: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    icon: PrimeSessionIcon,
    value: 'Prime Session',
  },
};

const LevelToTeachColor = {
  university: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'University',
  },
  elementary: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'Elementary',
  },
  high_school: { color: '#E0AD14', bgcolor: '#FFFBF0', value: 'High School' },
  middle_school: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'Middle School',
  },
};

const OneOnOneSessionDetails = ({ singleSessionDetails }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [alreadyDisputedOpen, setAlreadyDisputedOpen] = useState(false);

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Convert a UTC timestamp string to local Dayjs
  const toLocalFromUTC = utcString => {
    const utc = dayjs(utcString);
    return dayjs(new Date(utc.valueOf() - utc.toDate().getTimezoneOffset() * 60000));
  };

  // Format a time interval from UTC start and duration (minutes) using local time
  const formatTimeInterval = (startTime, lengthInMinutes) => {
    if (!startTime || !lengthInMinutes) return '---';
    const startTimeLocal = toLocalFromUTC(startTime);
    const endTimeLocal = dayjs(startTimeLocal).add(lengthInMinutes, 'minutes');
    const startFormat = startTimeLocal.format('h:mm a');
    const endFormat = endTimeLocal.format('h:mm a');
    return `${startFormat} - ${endFormat}`;
  };

  // Check if within a soft join window (used only for info toast)
  const isWithinJoinWindow = () => {
    if (!singleSessionDetails?.start_time) return false;
    const startTimeLocal = toLocalFromUTC(singleSessionDetails.start_time);
    const now = dayjs();
    const sessionLength = Number(singleSessionDetails.length_in_minutes || 0);
    return (
      startTimeLocal.diff(now, 'minute') <= 20 &&
      startTimeLocal.diff(now, 'minute') >= -sessionLength
    );
  };

  // Get the student data from the first student in the array
  const student = singleSessionDetails?.students?.[0] || {};
  const studentUser = student?.user || {};

  const checkDisputeThenOpen = async () => {
    try {
      const res = await axiosInstance.get(`/teachers/sessions/dispute/${singleSessionDetails?.id}`);
      if (res?.data && !res.data?.detail) {
        setAlreadyDisputedOpen(true);
        return;
      }
      if (res?.data?.detail === 'Dispute not found for this session') {
        setOpenReportModal(true);
        return;
      }
      setAlreadyDisputedOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'Dispute not found for this session' || err?.response?.status === 404) {
        setOpenReportModal(true);
      } else {
        setAlreadyDisputedOpen(true);
      }
    }
  };

  const handleAction = action => {
    if (action === 'reschedule') {
      setOpenRescheduleModal(true);
    } else if (action === 'cancel') {
      setOpenCancelModal(true);
    } else if (action === 'report') {
      checkDisputeThenOpen();
    }
    handleMenuClose();
  };

  const isUpcomingOrRescheduled = () => {
    return (
      singleSessionDetails?.status === 'upcoming' ||
      singleSessionDetails?.status === 'rescheduled' ||
      singleSessionDetails?.status === 'resheduled'
    );
  };

  const getStatusLabel = () => {
    if (singleSessionDetails?.status === 'upcoming') {
      return translate('sessionDetails', 'upcomingText');
    }
    if (
      singleSessionDetails?.status === 'rescheduled' ||
      singleSessionDetails?.status === 'resheduled'
    ) {
      return translate('sessionDetails', 'rescheduledText') || 'Rescheduled';
    }
    if (singleSessionDetails?.status === 'completed') {
      return translate('sessionDetails', 'completedText');
    }
    return translate('sessionDetails', 'cancelledText');
  };

  const handleJoinMeeting = () => {
    // Use meeting_link consistently
    const link = singleSessionDetails?.meeting_link;
    if (!link) {
      toast.error('Please check back at the session time');
      return;
    }
    if (!isWithinJoinWindow()) {
      toast.info('Please join at the scheduled session time');
    }
    window.open(link, '_blank');
  };

  return (
    <Box className="flex flex-col gap-4">
      <Box className="flex justify-between items-center">
        <Box className="flex flex-col gap-1">
          <Box className="flex items-center gap-2">
            <Typography
              className="navigation-text"
              onClick={() => navigate('/instructor/sessions')}
            >
              {translate('sessionDetails', 'sessionText')} {'>'}
              {'>'} {getStatusLabel()} {'>'}
              {'>'} Learner Profile {'>'}
              {'>'}
            </Typography>
            <Typography className="profile-text">
              {translate('sessionDetails', 'detailsText')}
            </Typography>
          </Box>
          <Box className="flex items-center gap-3">
            <Avatar
              data-testid="admin-avatar"
              src={studentUser?.profile_image || ''}
              alt={studentUser?.name || 'Student'}
              sx={{ width: '45px', height: '45px' }}
            />
            <Box className="flex flex-col">
              <Box className="flex items-center gap-4">
                <Typography className="student-title">{studentUser?.name || '---'}</Typography>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    height: '16px',
                    mt: 1,
                    borderColor: '#D5DEE3',
                  }}
                />
                <Box
                  style={{
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '10px',
                    color: SessionTypeColor[singleSessionDetails?.session_type]?.color || '#6b60e7',
                    fontFamily: 'Poppins !important',
                    fontSize: '14px !important',
                    fontStyle: 'normal !important',
                    fontWeight: '400 !important',
                    lineHeight: 'normal !important',
                  }}
                >
                  <img
                    src={
                      SessionTypeColor[singleSessionDetails?.session_type]?.icon ||
                      OneOnOneSessionIcon
                    }
                    alt={
                      SessionTypeColor[singleSessionDetails?.session_type]?.value || 'Session Type'
                    }
                    style={{ width: '16px', height: '16px' }}
                  />
                  <Typography
                    style={{
                      color: '#6b60e7',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      fontWeight: '600',
                    }}
                  >
                    {singleSessionDetails?.session_type
                      ? SessionTypeColor[singleSessionDetails?.session_type]?.value ||
                        singleSessionDetails.session_type
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, l => l.toUpperCase())
                      : '---'}
                  </Typography>
                </Box>
              </Box>
              <Box
                style={{
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '150px',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  backgroundColor: LevelToTeachColor[student?.school_level]?.bgcolor || '#F0EFFF',
                  color: LevelToTeachColor[student?.school_level]?.color || '#6B60E7',
                  fontFamily: 'Poppins !important',
                  fontSize: '14px !important',
                  fontStyle: 'normal !important',
                  fontWeight: '400 !important',
                  lineHeight: 'normal !important',
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {student?.school_level
                    ? LevelToTeachColor[student?.school_level]?.value ||
                      student?.school_level
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, l => l.toUpperCase())
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className="flex items-center gap-4">
          {singleSessionDetails.status !== 'cancelled' && (
            <IconButton data-testid="more-options-button" onClick={e => handleMenuClick(e)}>
              {anchorEl ? (
                <MoreHorizOutlinedIcon sx={{ color: '#0A5EF3' }} />
              ) : (
                <MoreHorizOutlinedIcon />
              )}
            </IconButton>
          )}
          {(singleSessionDetails.status === 'upcoming' ||
            singleSessionDetails.status === 'rescheduled' ||
            singleSessionDetails.status === 'resheduled' ||
            singleSessionDetails.status === 'completed') && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { width: '200px' },
              }}
            >
              {/* Show Reschedule only for one_on_one sessions when status is upcoming and NOT already rescheduled */}
              {singleSessionDetails?.session_type === 'one_on_one' &&
                singleSessionDetails.status === 'upcoming' &&
                singleSessionDetails.status !== 'rescheduled' &&
                singleSessionDetails.status !== 'resheduled' && (
                  <MenuItem onClick={() => handleAction('reschedule')}>
                    <Box className="flex gap-2 items-center">
                      <img src={RescheduleIcon} alt="view Details" />
                      <div>{translate('sessionDetails', 'rescheduleText')}</div>
                    </Box>
                  </MenuItem>
                )}

              {/* Show Cancel for one_on_one sessions when status is upcoming or rescheduled */}
              {singleSessionDetails?.session_type === 'one_on_one' &&
                (singleSessionDetails.status === 'upcoming' ||
                  singleSessionDetails.status === 'rescheduled' ||
                  singleSessionDetails.status === 'resheduled') && (
                  <>
                    <Divider sx={{ mx: 2 }} />
                    <MenuItem onClick={() => handleAction('cancel')}>
                      <Box className="flex gap-2 items-center">
                        <img src={CancelSessionIcon} alt="view Details" />
                        <div style={{ color: '#E14949' }}>
                          {translate('sessionDetails', 'cancelSessionText')}
                        </div>
                      </Box>
                    </MenuItem>
                  </>
                )}

              {/* Show Report for completed sessions */}
              {singleSessionDetails.status === 'completed' && (
                <MenuItem onClick={() => handleAction('report')}>
                  <Box className="flex gap-2 items-center">
                    <img src={ReportIcon} alt="Report" />
                    <div>{translate('sessionDetails', 'reportIssuesText')}</div>
                  </Box>
                </MenuItem>
              )}
            </Menu>
          )}
        </Box>
      </Box>
      <Box className="one-on-one-session-single-card-container">
        <Typography className="one-on-one-session-details-text">
          {translate('sessionDetails', 'sessionDetailsText')}
        </Typography>
        <Box className="flex flex-wrap items-center content-center gap-2 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
          <Box className="flex flex-col gap-2 w-[23%]">
            <Typography className="one-on-one-session-details-key-text">
              {translate('sessionDetails', 'subjectText')}
            </Typography>
            <Typography className="one-on-one-session-details-value-text">
              {singleSessionDetails?.subject ? singleSessionDetails?.subject : '---'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[23%]">
            <Typography className="one-on-one-session-details-key-text">
              {translate('sessionDetails', 'dateText')}
            </Typography>
            <Typography className="one-on-one-session-details-value-text">
              {singleSessionDetails?.start_time
                ? toLocalFromUTC(singleSessionDetails.start_time).format('DD-MM-YYYY')
                : '---'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[23%]">
            <Typography className="one-on-one-session-details-key-text">
              {translate('sessionDetails', 'timingText')}
            </Typography>
            <Typography className="one-on-one-session-details-value-text">
              {formatTimeInterval(
                singleSessionDetails?.start_time,
                singleSessionDetails?.length_in_minutes
              )}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[23%]">
            <Typography className="one-on-one-session-details-key-text">
              {translate('sessionDetails', 'sessionTypeText')}
            </Typography>
            <Typography
              className="one-on-one-session-details-value-text"
              sx={{
                color: SessionTypeColor[singleSessionDetails?.session_type]?.color || '#666',
              }}
            >
              {singleSessionDetails?.session_type
                ? SessionTypeColor[singleSessionDetails?.session_type]?.value ||
                  singleSessionDetails.session_type
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase())
                : '---'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Cancellation Details Section - Only show if status is cancelled */}
      {singleSessionDetails?.status === 'cancelled' && (
        <Box className="one-on-one-session-single-card-container">
          <Typography className="one-on-one-session-details-text">
            {translate('sessionDetails', 'cancellationDetailsText') || 'Cancellation Details'}
          </Typography>
          <Box className="flex flex-wrap items-center content-center gap-2 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
            <Box className="flex flex-col gap-2 w-[23%]">
              <Typography className="one-on-one-session-details-key-text">Cancelled By</Typography>
              <Typography className="one-on-one-session-cancelled-details-value-text">
                {singleSessionDetails?.cancelled_by === 'teacher'
                  ? 'Instructor'
                  : singleSessionDetails?.cancelled_by === 'student'
                    ? 'Learner'
                    : '---'}
              </Typography>
            </Box>
            <Box className="flex flex-col gap-2 w-[23%]">
              <Typography className="one-on-one-session-details-key-text">Cancelled on</Typography>
              <Typography className="one-on-one-session-cancelled-details-value-text">
                {singleSessionDetails?.updated_at
                  ? (() => {
                      return toLocalFromUTC(singleSessionDetails.updated_at).format('DD-MM-YYYY');
                    })()
                  : '---'}
              </Typography>
            </Box>
            <Box className="flex flex-col gap-2 w-[23%]">
              <Typography className="one-on-one-session-details-key-text">
                Cancelled Time
              </Typography>
              <Typography className="one-on-one-session-cancelled-details-value-text">
                {singleSessionDetails?.updated_at
                  ? (() => {
                      return toLocalFromUTC(singleSessionDetails.updated_at).format('hh:mm a');
                    })()
                  : '---'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Reasons Section - Only show if status is cancelled */}
      {singleSessionDetails?.status === 'cancelled' && (
        <Box className="one-on-one-session-single-card-container">
          <Typography className="one-on-one-session-details-text">Cancellation Reason</Typography>
          <Box className="flex flex-wrap items-start content-start gap-2 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
            <Typography className="one-on-one-session-details-value-text">
              {singleSessionDetails?.cancellation_reason ||
                'Urgency - I have to go out for a medical emergency.'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Koins Section - Only show if status is cancelled */}
      {singleSessionDetails?.status === 'cancelled' && singleSessionDetails?.penalty_koins > 0 && (
        <Box className="one-on-one-session-single-card-container">
          <Typography className="one-on-one-session-details-text" sx={{ color: '#E14949' }}>
            Koins - Deducted
          </Typography>
          <Box className="flex flex-wrap items-start content-start gap-2 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
            <Typography className="one-on-one-session-details-value-text">
              Since the cancellation was done within{' '}
              <span style={{ fontWeight: 'bold' }}>8 hours</span>,{' '}
              <span style={{ color: '#E14949' }}>{singleSessionDetails?.penalty_koins} koins</span>{' '}
              has been deducted from your wallet as a{' '}
              <span style={{ color: '#E14949' }}>penalties</span>.
            </Typography>
          </Box>
        </Box>
      )}

      <Box className="one-on-one-session-single-card-container">
        <Typography className="one-on-one-session-details-text">Description</Typography>
        <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
          <Box className="flex flex-col gap-2 w-[48%]">
            <Typography className="one-on-one-session-details-key-text">
              {translate('sessionDetails', 'detailsText')}
            </Typography>
            <Typography className="one-on-one-session-details-value-text">
              {singleSessionDetails?.description || '---'}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-2 w-[48%]">
            <Typography className="one-on-one-session-details-key-text">
              {translate('sessionDetails', 'documentText')}
            </Typography>
            <Box className="flex items-center gap-2">
              {/* Document Logo */}
              <img src={DocumentLogo} alt="Document" className="w-[18] h-[18]" />

              {/* Document Name */}
              <Typography className="personal-information-value-text">
                {singleSessionDetails?.file_url ? 'Document' : 'No document'}
              </Typography>

              {/* View Button */}
              {singleSessionDetails?.file_url && (
                <a
                  href={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(
                    singleSessionDetails.file_url
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="one-on-one-document-view-button"
                >
                  View
                </a>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className="chat-zoom-container">
        {singleSessionDetails.status !== 'cancelled' &&
          singleSessionDetails.status !== 'completed' && (
            <Box className="join-button" onClick={handleJoinMeeting}>
              <Box className="flex items-center gap-2">
                <img src={ZoomIcon} alt="Document" className="zoom-icon" color="#0A5EF3" />
                <Typography className="join-text">Start Session</Typography>
              </Box>
            </Box>
          )}
      </Box>
      {/* Reviews Section - Only show if status is completed and has reviews */}
      {singleSessionDetails?.status === 'completed' &&
        singleSessionDetails?.reviews &&
        singleSessionDetails.reviews.length > 0 && (
          <Box className="one-on-one-session-single-card-container">
            <Typography className="one-on-one-session-details-text">
              {translate('sessionDetails', 'reviewsText') || 'Reviews'}
            </Typography>
            <Box className="flex flex-col gap-4 w-[100%]">
              {singleSessionDetails.reviews.map((review, index) => (
                <Box
                  key={review.id || index}
                  className="flex flex-col gap-3 p-3 rounded-md bg-[#FAFAFA] w-[100%]"
                >
                  <Box className="flex items-center gap-3">
                    <Avatar
                      src={review.student?.profile_image || ''}
                      alt={review.student?.name || 'Student'}
                      sx={{ width: '40px', height: '40px' }}
                    />
                    <Box className="flex flex-col">
                      <Typography
                        className="one-on-one-session-details-value-text"
                        sx={{ fontWeight: '600' }}
                      >
                        {review.student?.name || '---'}
                      </Typography>
                      <Box className="flex items-center gap-1">
                        {[...Array(5)].map((_, starIndex) => (
                          <StarIcon
                            key={starIndex}
                            sx={{
                              color: starIndex < review.rating ? '#FFD700' : '#E0E0E0',
                              fontSize: '16px',
                            }}
                          />
                        ))}
                        <Typography
                          className="one-on-one-session-details-value-text"
                          sx={{ fontSize: '12px', color: '#666' }}
                        >
                          ({review.rating}/5)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {review.feedback && (
                    <Typography
                      className="one-on-one-session-details-value-text"
                      sx={{ fontSize: '14px', lineHeight: '1.5' }}
                    >
                      "{review.feedback}"
                    </Typography>
                  )}
                  <Typography
                    className="one-on-one-session-details-key-text"
                    sx={{ fontSize: '12px', color: '#666' }}
                  >
                    {review.created_at
                      ? dayjs(review.created_at).format('DD MMM YYYY, h:mm A')
                      : '---'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      <RescheduleSessionModal
        open={openRescheduleModal}
        onClose={() => setOpenRescheduleModal(false)}
        sessionId={singleSessionDetails?.id}
      />
      <CancelSessionModal
        open={openCancelModal}
        onClose={() => setOpenCancelModal(false)}
        sessionId={singleSessionDetails?.id}
      />
      <ReportModal
        open={openReportModal}
        onClose={() => setOpenReportModal(false)}
        sessionDetails={singleSessionDetails}
        onSuccess={() => {
          // You could refresh session details or show a notification here
          console.log('Report submitted successfully');
        }}
      />

      <Dialog open={alreadyDisputedOpen} onClose={() => setAlreadyDisputedOpen(false)}>
        <DialogTitle>You have already raised the dispute</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A dispute has already been created for this session.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlreadyDisputedOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OneOnOneSessionDetails;
